import React from "react";
import { Form } from "react-bootstrap";
import { FaRocket, FaExclamationCircle } from "react-icons/fa";
import {
  PLAN_PRICING,
  getGlobalApiCallsBounds,
  validateApiCallsForPlan,
} from "../../utils/planPricing";

const { minCalls, maxCalls } = getGlobalApiCallsBounds();

const ApiCallsSelector = ({
  value,
  onChange,
  selectedPlanBackendName,
  compact = false,
  billingCycle = "monthly",
}) => {
  const callsLabel =
    String(billingCycle).toLowerCase() === "yearly"
      ? "Yearly API Calls"
      : "Monthly API Calls";
  const planKey = selectedPlanBackendName
    ? String(selectedPlanBackendName).toUpperCase()
    : null;
  const planCfg = planKey ? PLAN_PRICING[planKey] : null;

  const effectiveMin = planCfg?.minCalls ?? minCalls;
  const effectiveMax = planCfg?.maxCalls ?? maxCalls;

  const validation = planKey
    ? validateApiCallsForPlan(planKey, value)
    : { valid: true };
  const isInvalid = planKey && !validation.valid && value !== "" && value != null;

  const handleChange = (e) => {
    const raw = e.target.value.replace(/,/g, "");
    if (raw === "") {
      onChange("");
      return;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return;
    if (n > effectiveMax) return;
    onChange(n);
  };

  const formContent = (
    <Form.Group className="mb-0">
      {compact ? (
        <div className="d-flex align-items-center gap-2 mb-2">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 32,
              height: 32,
              background: "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
              color: "#fff",
            }}
          >
            <FaRocket size={14} />
          </div>
          <div>
            <div className="fw-semibold small mb-0">{callsLabel}</div>
            <div className="text-muted" style={{ fontSize: "11px" }}>
              Min {effectiveMin.toLocaleString()} · Max{" "}
              {effectiveMax.toLocaleString()}
            </div>
          </div>
        </div>
      ) : (
        <>
          <Form.Label className="fw-bold">
            {callsLabel}
            {planCfg && (
              <span className="text-muted fw-normal ms-1 small d-block">
                Min {effectiveMin.toLocaleString()} – Max{" "}
                {effectiveMax.toLocaleString()}
              </span>
            )}
          </Form.Label>
        </>
      )}

      <Form.Control
        type="text"
        inputMode="numeric"
        placeholder={`${effectiveMin.toLocaleString()}`}
        value={
          value === "" || value == null ? "" : Number(value).toLocaleString()
        }
        onChange={handleChange}
        size={compact ? "sm" : "lg"}
        isInvalid={isInvalid}
        style={
          compact
            ? {
                fontWeight: 600,
                textAlign: "center",
                borderRadius: "8px",
              }
            : undefined
        }
      />

      {isInvalid && (
        <div className="text-danger small mt-1 d-flex align-items-start gap-1">
          <FaExclamationCircle className="mt-1 flex-shrink-0" size={12} />
          <span>{validation.error}</span>
        </div>
      )}

      {!compact && (
        <Form.Text className="text-muted">
          Final price = Base price + (extra calls × per-call rate) + 18% GST.
        </Form.Text>
      )}
    </Form.Group>
  );

  if (compact) {
    return (
      <div
        className="mb-3 p-3 rounded-3"
        style={{
          background: isInvalid
            ? "linear-gradient(135deg, #fff5f5 0%, #fff 100%)"
            : "linear-gradient(135deg, #f0f7ff 0%, #fff 100%)",
          border: isInvalid ? "1px solid #f5c2c7" : "1px solid #cfe2ff",
          boxShadow: "0 2px 8px rgba(0, 123, 255, 0.08)",
        }}
      >
        {formContent}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="card border-primary shadow-sm">
        <div className="card-body">{formContent}</div>
      </div>
    </div>
  );
};

export default ApiCallsSelector;
